import React, { useRef } from "react";
import * as Ariakit from "@ariakit/react";
import { Bell } from "lucide-react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Color } from "../../constants/color";
import { ZIndex } from "../../constants/z-index";
import { DashboardSummary } from "../../types";
import { conteggioInScadenza } from "../../features/dashboard/riepilogo";
import { urlKpi } from "../../features/certificate/pipeline";

const Griglia = styled.div<{ $larghezzaPiena: boolean }>`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  gap: 2rem;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1rem;
  ${(props) => (props.$larghezzaPiena ? "width: 100%;" : "")}
`;

const Intestazione = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
  color: ${Color.LIGHT_BLUE};
`;

const RigaTitolo = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 1em;
`;

const Titolo = styled.p`
  font-size: 2rem;
  font-weight: bolder;
  line-height: 1em;
`;

const KpiLayout = styled.div<{ $color: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.$color};
  cursor: pointer;
  /* Fa ereditare il cursore (es. pointer sui KPI cliccabili) anche a label e
     numeri sottostanti, che altrimenti restano col cursore di default */
  & * {
    cursor: inherit;
  }
`;

const KpiLabel = styled.label<{ $fontSize: string }>`
  font-weight: bold;
  font-size: ${(props) => props.$fontSize};
  text-wrap: nowrap;
`;

const SelettoreMesi = styled.div`
  display: flex;
  gap: 0.1em;
  align-items: center;
  justify-content: center;
`;

const SelectMesi = styled.select`
  border: none;
  color: ${Color.ORANGE};
  font-weight: bold;
  outline: none;
`;

const Hovercard = styled(Ariakit.Hovercard)<{ $color: string }>`
  background-color: ${(props) => props.$color};
  color: ${Color.WHITE};
  padding: 1em;
  border-radius: 8px;
  z-index: ${ZIndex.TOOLTIP};
`;

// Il riquadro di un KPI: cliccabile e raggiungibile da tastiera (Invio o
// spazio), come un pulsante.
function RiquadroKpi({
  colore,
  onAttiva,
  children,
}: {
  colore: string;
  onAttiva: (e: React.SyntheticEvent) => void;
  children: React.ReactNode;
}) {
  return (
    <KpiLayout
      $color={colore}
      role="button"
      tabIndex={0}
      onClick={onAttiva}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onAttiva(e);
        }
      }}
    >
      {children}
    </KpiLayout>
  );
}

type Props = {
  riepilogo: DashboardSummary;
  // Finestra degli in scadenza (1, 2 o 6 mesi) scelta nella tendina.
  mesi: number;
  onMesiChange: (mesi: number) => void;
  // La griglia occupa tutta la larghezza del contenitore (tab Scadenza)
  // invece di adattarsi al contenuto (dashboard).
  larghezzaPiena?: boolean;
};

// Il blocco KPI degli attestati (mancanti, scaduti, in scadenza, validi),
// condiviso fra dashboard e tab Scadenza. Il click su un KPI porta alla tab
// Scadenza filtrata sullo stato del KPI con la navigazione dell'applicazione,
// senza ricaricare la pagina.
export default function KpiAttestati({
  riepilogo,
  mesi,
  onMesiChange,
  larghezzaPiena = false,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const etichettaMesi = (m: number) =>
    m === 1 ? t("kpi-attestati-un-mese") : t("kpi-attestati-n-mesi", { n: m });
  // La tendina dei mesi sta dentro il KPI in scadenza: un click su di lei (o
  // sulla sua etichetta) cambia la finestra e non deve navigare.
  const selettoreMesi = useRef<HTMLDivElement>(null);
  const vaiAgliInScadenza = (e: React.SyntheticEvent) => {
    if (selettoreMesi.current?.contains(e.target as Node)) return;
    navigate(urlKpi("in_scadenza", mesi));
  };

  return (
    <Griglia $larghezzaPiena={larghezzaPiena}>
      <Intestazione>
        <RigaTitolo>
          <Titolo>{t("dashboard-certificates")}</Titolo>
        </RigaTitolo>
      </Intestazione>
      {/* KPI MANCANTI */}
      <Ariakit.HovercardProvider>
        <Ariakit.HovercardAnchor render={<div />}>
          <RiquadroKpi
            colore={Color.RED}
            onAttiva={() => navigate(urlKpi("mancante"))}
          >
            <Bell color={Color.RED} width={32} height={32} fill={Color.RED} />
            <KpiLabel $fontSize="1rem">{t("dashboard-new")}</KpiLabel>
            <KpiLabel $fontSize="1.2rem">{riepilogo.mancanti}</KpiLabel>
          </RiquadroKpi>
        </Ariakit.HovercardAnchor>
        <Hovercard $color={Color.RED}>
          <Ariakit.HovercardHeading render={<h2 />}>
            {t("dashboard-new")}
          </Ariakit.HovercardHeading>
          <p>{t("kpi-attestati-mancanti-descrizione")}</p>
          <p>{t("kpi-attestati-mancanti-nota")}</p>
        </Hovercard>
      </Ariakit.HovercardProvider>
      {/* KPI SCADUTI */}
      <Ariakit.HovercardProvider>
        <Ariakit.HovercardAnchor render={<div />}>
          <RiquadroKpi
            colore={Color.RED}
            onAttiva={() => navigate(urlKpi("scaduto"))}
          >
            <Bell color={Color.RED} width={32} height={32} fill={Color.RED} />
            <KpiLabel $fontSize="1rem">{t("dashboard-expired")}</KpiLabel>
            <KpiLabel $fontSize="1.2rem">{riepilogo.scaduti}</KpiLabel>
          </RiquadroKpi>
        </Ariakit.HovercardAnchor>
        <Hovercard $color={Color.RED}>
          <Ariakit.HovercardHeading render={<h2 />}>
            {t("dashboard-expired")}
          </Ariakit.HovercardHeading>
          <p>{t("kpi-attestati-scaduti-descrizione")}</p>
        </Hovercard>
      </Ariakit.HovercardProvider>
      {/* KPI IN SCADENZA */}
      <Ariakit.HovercardProvider>
        <Ariakit.HovercardAnchor render={<div />}>
          <RiquadroKpi colore={Color.ORANGE} onAttiva={vaiAgliInScadenza}>
            <Bell
              color={Color.ORANGE}
              width={32}
              height={32}
              fill={Color.ORANGE}
            />
            <SelettoreMesi ref={selettoreMesi}>
              <KpiLabel $fontSize="1rem" htmlFor="select_expire">
                {t("dashboard-expiring")}
              </KpiLabel>
              <SelectMesi
                id="select_expire"
                value={mesi}
                onChange={(e) => onMesiChange(parseInt(e.target.value))}
              >
                <option value="1">{etichettaMesi(1)}</option>
                <option value="2">{etichettaMesi(2)}</option>
                <option value="6">{etichettaMesi(6)}</option>
              </SelectMesi>
            </SelettoreMesi>
            <KpiLabel $fontSize="1rem">
              {conteggioInScadenza(riepilogo, mesi)}
            </KpiLabel>
          </RiquadroKpi>
        </Ariakit.HovercardAnchor>
        <Hovercard $color={Color.ORANGE}>
          <Ariakit.HovercardHeading render={<h2 />}>
            {t("kpi-attestati-in-scadenza-titolo", { mesi: etichettaMesi(mesi) })}
          </Ariakit.HovercardHeading>
          <p>
            {t("kpi-attestati-in-scadenza-descrizione", {
              mesi: etichettaMesi(mesi),
            })}
          </p>
        </Hovercard>
      </Ariakit.HovercardProvider>
      {/* KPI VALIDI */}
      <Ariakit.HovercardProvider>
        <Ariakit.HovercardAnchor render={<div />}>
          <RiquadroKpi
            colore={Color.GREEN}
            onAttiva={() => navigate(urlKpi("valido"))}
          >
            <Bell
              color={Color.GREEN}
              width={32}
              height={32}
              fill={Color.GREEN}
            />
            <KpiLabel $fontSize="1rem">{t("dashboard-valid")}</KpiLabel>
            <KpiLabel $fontSize="1.2rem">{riepilogo.validi}</KpiLabel>
          </RiquadroKpi>
        </Ariakit.HovercardAnchor>
        <Hovercard $color={Color.GREEN}>
          <Ariakit.HovercardHeading render={<h2 />}>
            {t("dashboard-valid")}
          </Ariakit.HovercardHeading>
          <p>{t("kpi-attestati-validi-descrizione")}</p>
        </Hovercard>
      </Ariakit.HovercardProvider>
    </Griglia>
  );
}
